import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { tvLive, getTvSettings, listTvProfiles, createTvProfile, updateTvProfile, deleteTvProfile, listCuttingSimDays } from "@/lib/api";
import { Gauge, Timer, Target, Scissors, RefreshCw, CheckCircle2, Radio, Hourglass, Settings, X, BarChart3, LogOut, Tv, Plus, Pencil, Trash2, Check, AlertTriangle, Calendar as CalendarIcon, ChevronDown } from "lucide-react";

const REFRESH_MS = 30_000; // 30s soft poll

/** Color presets — apply all 7 colours at once with a single click. Each preset
 * targets a distinct production environment (factory floor, office, alert mode). */
const COLOR_PRESETS = [
  {
    id: "verde-classico",
    label: "Verde Clássico",
    hint: "Padrão equilibrado (default)",
    swatches: ["#047857", "#0369a1", "#10b981", "#f59e0b", "#fb7185"],
    colors: {
      color_intra_good: "#047857",
      color_intra_warn: "#d97706",
      color_intra_bad: "#b91c1c",
      color_between_markers: "#0369a1",
      color_between_good: "#047857",
      color_between_warn: "#d97706",
      color_between_bad: "#b91c1c",
      color_footer_cutting: "#10b981",
      color_footer_stopped: "#f59e0b",
      color_footer_between: "#fb7185",
    },
  },
  {
    id: "industrial",
    label: "Industrial",
    hint: "Vermelhos/laranjas — chão de fábrica activo",
    swatches: ["#16a34a", "#0891b2", "#15803d", "#ea580c", "#dc2626"],
    colors: {
      color_intra_good: "#15803d",
      color_intra_warn: "#ea580c",
      color_intra_bad: "#9f1239",
      color_between_markers: "#0891b2",
      color_between_good: "#15803d",
      color_between_warn: "#ea580c",
      color_between_bad: "#9f1239",
      color_footer_cutting: "#16a34a",
      color_footer_stopped: "#ea580c",
      color_footer_between: "#dc2626",
    },
  },
  {
    id: "corporate-azul",
    label: "Corporate Azul",
    hint: "Tom corporativo — sala de reuniões",
    swatches: ["#1d4ed8", "#0e7490", "#2563eb", "#7c3aed", "#475569"],
    colors: {
      color_intra_good: "#1d4ed8",
      color_intra_warn: "#7c3aed",
      color_intra_bad: "#be123c",
      color_between_markers: "#0e7490",
      color_between_good: "#1d4ed8",
      color_between_warn: "#7c3aed",
      color_between_bad: "#be123c",
      color_footer_cutting: "#2563eb",
      color_footer_stopped: "#7c3aed",
      color_footer_between: "#475569",
    },
  },
  {
    id: "alerta-alto",
    label: "Alerta Alto",
    hint: "Cores intensas — máxima visibilidade à distância",
    swatches: ["#22c55e", "#06b6d4", "#a3e635", "#facc15", "#f43f5e"],
    colors: {
      color_intra_good: "#22c55e",
      color_intra_warn: "#facc15",
      color_intra_bad: "#f43f5e",
      color_between_markers: "#06b6d4",
      color_between_good: "#22c55e",
      color_between_warn: "#facc15",
      color_between_bad: "#f43f5e",
      color_footer_cutting: "#a3e635",
      color_footer_stopped: "#facc15",
      color_footer_between: "#f43f5e",
    },
  },
  {
    id: "mono-grafite",
    label: "Mono Grafite",
    hint: "Monocromático, foco no número",
    swatches: ["#0f172a", "#334155", "#1e293b", "#64748b", "#94a3b8"],
    colors: {
      color_intra_good: "#0f172a",
      color_intra_warn: "#475569",
      color_intra_bad: "#b91c1c",
      color_between_markers: "#334155",
      color_between_good: "#0f172a",
      color_between_warn: "#475569",
      color_between_bad: "#b91c1c",
      color_footer_cutting: "#1e293b",
      color_footer_stopped: "#64748b",
      color_footer_between: "#94a3b8",
    },
  },
  {
    id: "pastel-suave",
    label: "Pastel Suave",
    hint: "Tons suaves — ambiente cliente / lobby",
    swatches: ["#86efac", "#93c5fd", "#fcd34d", "#fda4af", "#c4b5fd"],
    colors: {
      color_intra_good: "#34d399",
      color_intra_warn: "#fcd34d",
      color_intra_bad: "#fb7185",
      color_between_markers: "#60a5fa",
      color_between_good: "#34d399",
      color_between_warn: "#fcd34d",
      color_between_bad: "#fb7185",
      color_footer_cutting: "#86efac",
      color_footer_stopped: "#fcd34d",
      color_footer_between: "#fda4af",
    },
  },
  {
    id: "neon-vibrante",
    label: "Neon Vibrante",
    hint: "Saturado — demos e eventos",
    swatches: ["#00ff88", "#00d4ff", "#ffd400", "#ff7a00", "#ff0080"],
    colors: {
      color_intra_good: "#00ff88",
      color_intra_warn: "#ffd400",
      color_intra_bad: "#ff0080",
      color_between_markers: "#00d4ff",
      color_between_good: "#00ff88",
      color_between_warn: "#ffd400",
      color_between_bad: "#ff0080",
      color_footer_cutting: "#00ff88",
      color_footer_stopped: "#ff7a00",
      color_footer_between: "#ff0080",
    },
  },
  {
    id: "oceano-frio",
    label: "Oceano Frio",
    hint: "Tons frios — ambientes climatizados/laboratoriais",
    swatches: ["#0d9488", "#0284c7", "#06b6d4", "#3b82f6", "#6366f1"],
    colors: {
      color_intra_good: "#0d9488",
      color_intra_warn: "#0284c7",
      color_intra_bad: "#7c3aed",
      color_between_markers: "#0e7490",
      color_between_good: "#0d9488",
      color_between_warn: "#0284c7",
      color_between_bad: "#7c3aed",
      color_footer_cutting: "#06b6d4",
      color_footer_stopped: "#3b82f6",
      color_footer_between: "#6366f1",
    },
  },
];

/** Apply alpha to a hex color (#rgb / #rrggbb / #rrggbbaa) and return rgba(). */
function hexA(hex, alpha = 1) {
  if (!hex) return `rgba(15,23,42,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length === 8) h = h.substring(0, 6);
  const num = parseInt(h, 16);
  if (Number.isNaN(num)) return `rgba(15,23,42,${alpha})`;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function fmtClock(d) {
  return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/Lisbon" });
}
function fmtDate(d) {
  return d.toLocaleDateString("pt-PT", { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: "Europe/Lisbon" });
}
/** Parse a YYYY-MM-DD string into a "DD/MM/YYYY" short label (TV title chip). */
function fmtPrettyDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}
/** Full localised weekday/month label from YYYY-MM-DD. */
function fmtDateFromIso(iso) {
  if (!iso) return "—";
  try {
    // Use noon UTC so the date stays stable across timezones.
    const dt = new Date(`${iso}T12:00:00`);
    return dt.toLocaleDateString("pt-PT", { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZone: "Europe/Lisbon" });
  } catch {
    return iso;
  }
}
function fmtMinutes(min) {
  if (!min || min <= 0) return "0min";
  if (min < 1) return `${Math.round(min * 60)}s`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m}min`;
}
function fmtAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "agora";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `há ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `há ${m}min`;
  const h = Math.floor(m / 60);
  return `há ${h}h${String(m % 60).padStart(2, "0")}`;
}

/** Format a future ISO timestamp as a relative "em Xmin" / "em Xh" string. */
function fmtInFuture(iso, now) {
  if (!iso) return "—";
  const target = new Date(iso).getTime();
  const ms = target - now.getTime();
  if (ms <= 0) return "a iniciar";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `em ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `em ${m}min`;
  const h = Math.floor(m / 60);
  return `em ${h}h${String(m % 60).padStart(2, "0")}`;
}

/** Format an ISO timestamp as "HH:MM" in Europe/Lisbon (independent of the
 * browser timezone — important for TVs whose OS clock might be in UTC). */
function fmtClockHM(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Lisbon" });
  } catch {
    return "—";
  }
}

/** Build a Date for TODAY at the given HH:MM:SS and return relative "há Xmin". */
function fmtAgoFromHHMMSS(hhmmss, now) {
  if (!hhmmss) return "";
  const parts = String(hhmmss).split(":").map((s) => parseInt(s, 10) || 0);
  const [h, m, s] = [parts[0] || 0, parts[1] || 0, parts[2] || 0];
  const ref = new Date(now);
  ref.setHours(h, m, s, 0);
  const diffMs = now.getTime() - ref.getTime();
  if (diffMs < 0) return "no futuro";
  const totalS = Math.floor(diffMs / 1000);
  if (totalS < 60) return `há ${totalS}s`;
  const mins = Math.floor(totalS / 60);
  if (mins < 60) return `há ${mins}min`;
  const hrs = Math.floor(mins / 60);
  return `há ${hrs}h${String(mins % 60).padStart(2, "0")}`;
}

/** Big-number card with TWO values side-by-side (for showing related metrics like Real & Média speeds). */
function DualKPI({ icon: Icon, label, leftLabel, leftValue, leftUnit, leftColor, leftAccent, rightLabel, rightValue, rightUnit, rightColor, rightAccent, valueFontVw = 9, leftFontVw, rightFontVw, testid }) {
  const lFont = leftFontVw ?? valueFontVw;
  const rFont = rightFontVw ?? valueFontVw;
  return (
    <div
      className="relative rounded-3xl bg-white border border-slate-200 shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)] overflow-hidden flex flex-col min-h-0 p-3 lg:p-4 xl:p-6"
      data-testid={testid}
    >
      <span className={`absolute top-0 left-0 w-1/2 h-1 ${leftAccent}`} />
      <span className={`absolute top-0 right-0 w-1/2 h-1 ${rightAccent}`} />
      <div className="flex items-center gap-3 text-slate-700 uppercase tracking-[0.18em] font-bold" style={{ fontSize: "clamp(0.85rem, 1.4vmin, 1.4rem)" }}>
        <Icon className="text-slate-500" style={{ width: "clamp(1.2rem, 1.8vmin, 1.9rem)", height: "clamp(1.2rem, 1.8vmin, 1.9rem)" }} />
        <span>{label}</span>
      </div>
      <div className="flex-1 grid grid-cols-[2fr_1fr] gap-2 lg:gap-4 items-center min-h-0">
        <div className="flex flex-col items-center justify-center min-w-0 relative">
          <div className="flex items-start gap-2 leading-none">
            <span
              className={`font-extrabold tabular-nums ${leftColor}`}
              style={{ fontSize: `clamp(3.6rem, ${lFont * 1.53}vmin, 15.5rem)`, fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 }}
              data-testid={`${testid}-left`}
            >
              {leftValue}
            </span>
            <div className="flex flex-col items-start gap-1">
              <span className="uppercase tracking-wider text-slate-500 font-bold whitespace-nowrap" style={{ fontSize: "clamp(0.65rem, 1vmin, 1rem)" }}>
                {leftLabel}
              </span>
              {leftUnit && (
                <span className="text-slate-500 font-bold whitespace-nowrap" style={{ fontSize: "clamp(0.9rem, 1.6vmin, 1.6rem)" }}>
                  {leftUnit}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center min-w-0 border-l border-slate-200 opacity-80">
          <div className="flex items-start gap-1 leading-none">
            <span
              className={`font-bold tabular-nums ${rightColor}`}
              style={{ fontSize: `clamp(2.2rem, ${rFont * 1.53}vmin, 7.2rem)`, fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 }}
              data-testid={`${testid}-right`}
            >
              {rightValue}
            </span>
            <div className="flex flex-col items-start gap-0.5">
              <span className="uppercase tracking-wider text-slate-500 font-bold whitespace-nowrap" style={{ fontSize: "clamp(0.6rem, 0.9vmin, 0.9rem)" }}>
                {rightLabel}
              </span>
              {rightUnit && (
                <span className="text-slate-500 font-bold whitespace-nowrap" style={{ fontSize: "clamp(0.75rem, 1.3vmin, 1.3rem)" }}>
                  {rightUnit}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Big-number card on a light surface. */
function BigKPI({ icon: Icon, label, value, unit, subText, metaTag, valueColor = "text-slate-900", iconColor = "text-slate-500", accentBar = "bg-slate-300", valueFontVw = 8, unitFontVw = 2.6, testid, customColor }) {
  // When customColor is provided, override Tailwind classes with inline styles.
  // Cap font size by BOTH viewport width AND height so cards never push the
  // page below the fold in fullscreen mode on short/wide displays.
  // Use a conservative vmin-based cap so 4:3 / 5:4 monitors (factory TVs)
  // do NOT end up with hidden text because the value exceeds the card height.
  const valueStyle = {
    fontSize: `clamp(3.6rem, ${valueFontVw * 1.8}vmin, 18rem)`,
    fontFamily: "'Space Grotesk', sans-serif",
    lineHeight: 1,
    ...(customColor ? { color: customColor } : {}),
  };
  const unitStyle = {
    fontSize: `clamp(1.9rem, ${unitFontVw * 1.8}vmin, 7.2rem)`,
    fontFamily: "'Space Grotesk', sans-serif",
    lineHeight: 1,
    ...(customColor ? { color: customColor, opacity: 0.85 } : {}),
  };
  const barStyle = customColor ? { background: customColor } : undefined;
  return (
    <div
      className="relative rounded-3xl bg-white border border-slate-200 shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)] overflow-hidden flex flex-col min-h-0 p-3 lg:p-4 xl:p-6"
      data-testid={testid}
    >
      <span className={`absolute top-0 left-0 right-0 h-1 ${customColor ? '' : accentBar}`} style={barStyle} />
      <div className="flex items-start justify-between gap-3 text-slate-700 uppercase tracking-[0.18em] font-bold" style={{ fontSize: "clamp(0.85rem, 1.4vmin, 1.4rem)" }}>
        <div className="flex items-center gap-3">
          <Icon className={`${customColor ? '' : iconColor}`} style={{ width: "clamp(1.2rem, 1.8vmin, 1.9rem)", height: "clamp(1.2rem, 1.8vmin, 1.9rem)", ...(customColor ? { color: customColor } : {}) }} />
          <span>{label}</span>
        </div>
        {metaTag && (
          <span
            className="px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700 font-semibold tracking-wide normal-case tabular-nums"
            style={{ fontSize: "clamp(0.7rem, 1vmin, 1rem)" }}
            data-testid={`${testid}-meta`}
          >
            {metaTag}
          </span>
        )}
      </div>
      <div className="flex-1 grid place-items-center">
        <div className="flex items-baseline gap-3 leading-none">
          <span
            className={`font-extrabold tabular-nums ${customColor ? '' : valueColor}`}
            style={valueStyle}
          >
            {value}
          </span>
          {unit && (
            <span
              className={`font-semibold ${customColor ? '' : 'text-slate-500'}`}
              style={unitStyle}
            >
              {unit}
            </span>
          )}
        </div>
      </div>
      {subText && (
        <div className="text-xs lg:text-sm text-slate-500 font-medium tabular-nums text-center pb-1" data-testid={`${testid}-sub`}>
          {subText}
        </div>
      )}
    </div>
  );
}

export default function TVPage() {
  const nav = useNavigate();
  const { profileSlug } = useParams();
  const activeSlug = profileSlug || "default";
  const [live, setLive] = useState(null);
  const [now, setNow] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({
    target_intra_pct_of_cutting: 50, target_between_per_cut_min: 2.0,
    intra_deviation_multiplier: 1.0, between_deviation_multiplier: 1.0,
    threshold_intra_good_pct: 100, threshold_intra_warn_pct: 80,
    tier_intra_good_pct: 50, tier_intra_warn_pct: 75,
    tier_between_good_min: 2.0, tier_between_warn_min: 3.0,
    color_intra_good: "#047857", color_intra_warn: "#d97706", color_intra_bad: "#b91c1c",
    color_between_markers: "#0369a1",
    color_between_good: "#047857", color_between_warn: "#d97706", color_between_bad: "#b91c1c",
    color_footer_cutting: "#10b981", color_footer_stopped: "#f59e0b", color_footer_between: "#fb7185",
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [showProfiles, setShowProfiles] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [renamingSlug, setRenamingSlug] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  // Operator-reported anomaly adjustment toggle (persists across reloads)
  const [adjusted, setAdjusted] = useState(() => {
    try { return localStorage.getItem("tv:adjusted") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("tv:adjusted", adjusted ? "1" : "0"); } catch (_) {}
  }, [adjusted]);
  // Selected date: null = today (live mode). Otherwise YYYY-MM-DD.
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [availableDays, setAvailableDays] = useState([]); // [{date, total_cuts, ...}]
  // Toggle: show the "Presets de cor" block inside the settings modal (hidden by default)
  const [showColorPresets, setShowColorPresets] = useState(false);
  const lastFetchRef = useRef(Date.now());
  const pollRef = useRef(null);
  const liveRef = useRef(null);
  const clockRef = useRef(null);

  const fetchProfiles = useCallback(async () => {
    try {
      const list = await listTvProfiles();
      setProfiles(list || []);
    } catch (e) {
      // non-blocking
    }
  }, []);

  const fetchAvailableDays = useCallback(async () => {
    try {
      const list = await listCuttingSimDays();
      // Sort newest first
      const sorted = (list || []).slice().sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setAvailableDays(sorted);
    } catch (e) {
      // non-blocking
    }
  }, []);

  const fetchLive = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await tvLive(activeSlug, { adjusted, date: selectedDate || undefined });
      setLive(data);
      liveRef.current = data;
      setError(null);
    } catch (e) {
      setError(e?.message || "erro de rede");
    } finally {
      setRefreshing(false);
      lastFetchRef.current = Date.now();
    }
  }, [activeSlug, adjusted, selectedDate]);

  const openSettings = useCallback(async () => {
    // Look up the active profile in the loaded list (includes colors).
    const activeProfile = profiles.find((p) => p.slug === activeSlug);
    if (activeProfile) {
      setSettingsDraft({
        target_intra_pct_of_cutting: activeProfile.target_intra_pct_of_cutting ?? 50,
        target_between_per_cut_min: activeProfile.target_between_per_cut_min ?? 2.0,
        intra_deviation_multiplier: activeProfile.intra_deviation_multiplier ?? 1.0,
        between_deviation_multiplier: activeProfile.between_deviation_multiplier ?? 1.0,
        threshold_intra_good_pct: activeProfile.threshold_intra_good_pct ?? 100,
        threshold_intra_warn_pct: activeProfile.threshold_intra_warn_pct ?? 80,
        tier_intra_good_pct: activeProfile.tier_intra_good_pct ?? 50,
        tier_intra_warn_pct: activeProfile.tier_intra_warn_pct ?? 75,
        tier_between_good_min: activeProfile.tier_between_good_min ?? 2.0,
        tier_between_warn_min: activeProfile.tier_between_warn_min ?? 3.0,
        color_intra_good: activeProfile.color_intra_good || activeProfile.color_intra || "#047857",
        color_intra_warn: activeProfile.color_intra_warn || "#d97706",
        color_intra_bad: activeProfile.color_intra_bad || "#b91c1c",
        color_between_markers: activeProfile.color_between_markers || "#0369a1",
        color_between_good: activeProfile.color_between_good || activeProfile.color_between_markers || "#047857",
        color_between_warn: activeProfile.color_between_warn || "#d97706",
        color_between_bad: activeProfile.color_between_bad || "#b91c1c",
        color_footer_cutting: activeProfile.color_footer_cutting || "#10b981",
        color_footer_stopped: activeProfile.color_footer_stopped || "#f59e0b",
        color_footer_between: activeProfile.color_footer_between || "#fb7185",
      });
    } else {
      // Fallback to legacy endpoint values + default colors
      try {
        const s = await getTvSettings(activeSlug);
        setSettingsDraft({
          target_intra_pct_of_cutting: s.target_intra_pct_of_cutting ?? 50,
          target_between_per_cut_min: s.target_between_per_cut_min ?? 2.0,
          intra_deviation_multiplier: s.intra_deviation_multiplier ?? 1.0,
          between_deviation_multiplier: s.between_deviation_multiplier ?? 1.0,
          threshold_intra_good_pct: 100, threshold_intra_warn_pct: 80,
          tier_intra_good_pct: 50, tier_intra_warn_pct: 75,
          tier_between_good_min: 2.0, tier_between_warn_min: 3.0,
          color_intra_good: live?.profile?.colors?.intra_good || "#047857",
          color_intra_warn: live?.profile?.colors?.intra_warn || "#d97706",
          color_intra_bad: live?.profile?.colors?.intra_bad || "#b91c1c",
          color_between_markers: live?.profile?.colors?.between_markers || "#0369a1",
          color_between_good: live?.profile?.colors?.between_good || "#047857",
          color_between_warn: live?.profile?.colors?.between_warn || "#d97706",
          color_between_bad: live?.profile?.colors?.between_bad || "#b91c1c",
          color_footer_cutting: live?.profile?.colors?.footer_cutting || "#10b981",
          color_footer_stopped: live?.profile?.colors?.footer_stopped || "#f59e0b",
          color_footer_between: live?.profile?.colors?.footer_between || "#fb7185",
        });
      } catch {
        setSettingsDraft({
          target_intra_pct_of_cutting: 50, target_between_per_cut_min: 2.0,
          intra_deviation_multiplier: 1.0, between_deviation_multiplier: 1.0,
          threshold_intra_good_pct: 100, threshold_intra_warn_pct: 80,
          tier_intra_good_pct: 50, tier_intra_warn_pct: 75,
          tier_between_good_min: 2.0, tier_between_warn_min: 3.0,
          color_intra_good: "#047857", color_intra_warn: "#d97706", color_intra_bad: "#b91c1c",
          color_between_markers: "#0369a1",
          color_between_good: "#047857", color_between_warn: "#d97706", color_between_bad: "#b91c1c",
          color_footer_cutting: "#10b981", color_footer_stopped: "#f59e0b", color_footer_between: "#fb7185",
        });
      }
    }
    setShowSettings(true);
  }, [live, activeSlug, profiles]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      // Update via the profile endpoint so colors are also persisted.
      const activeProfile = profiles.find((p) => p.slug === activeSlug);
      // Entre Marcadas: a meta legacy é sincronizada com o patamar Bom (já que o
      // sistema de patamares substitui a meta clássica para este KPI).
      // Intra Marcadas: a meta (target_intra_pct_of_cutting) é definida directamente
      // pelo utilizador — o KPI mostra a EFICIÊNCIA (score), e os patamares são
      // limites do score.
      const tierBetweenGood = Math.max(0, Number(settingsDraft.tier_between_good_min)) || 2.0;
      await updateTvProfile(activeSlug, {
        name: activeProfile?.name || activeSlug,
        slug: activeSlug,
        target_intra_pct_of_cutting: Math.max(0.01, Number(settingsDraft.target_intra_pct_of_cutting)) || 50,
        target_between_per_cut_min: tierBetweenGood,
        intra_deviation_multiplier: Math.max(0, Number(settingsDraft.intra_deviation_multiplier)) || 1.0,
        between_deviation_multiplier: Math.max(0, Number(settingsDraft.between_deviation_multiplier)) || 1.0,
        threshold_intra_good_pct: Math.max(0, Number(settingsDraft.threshold_intra_good_pct)) || 0,
        threshold_intra_warn_pct: Math.max(0, Number(settingsDraft.threshold_intra_warn_pct)) || 0,
        tier_intra_good_pct: Math.max(0, Number(settingsDraft.tier_intra_good_pct)) || 0,
        tier_intra_warn_pct: Math.max(0, Number(settingsDraft.tier_intra_warn_pct)) || 0,
        tier_between_good_min: tierBetweenGood,
        tier_between_warn_min: Math.max(0, Number(settingsDraft.tier_between_warn_min)) || 0,
        color_intra_good: settingsDraft.color_intra_good,
        color_intra_warn: settingsDraft.color_intra_warn,
        color_intra_bad: settingsDraft.color_intra_bad,
        color_between_markers: settingsDraft.color_between_markers,
        color_between_good: settingsDraft.color_between_good,
        color_between_warn: settingsDraft.color_between_warn,
        color_between_bad: settingsDraft.color_between_bad,
        color_footer_cutting: settingsDraft.color_footer_cutting,
        color_footer_stopped: settingsDraft.color_footer_stopped,
        color_footer_between: settingsDraft.color_footer_between,
      });
      setShowSettings(false);
      await Promise.all([fetchLive(), fetchProfiles()]);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Erro ao guardar definições");
    } finally {
      setSavingSettings(false);
    }
  };

  // Profile management
  const handleCreateProfile = async () => {
    const name = newProfileName.trim();
    if (!name) return;
    setProfileBusy(true);
    try {
      const p = await createTvProfile({
        name,
        target_intra_pct_of_cutting: 50,
        target_between_per_cut_min: 2.0,
        intra_deviation_multiplier: 1.0,
        between_deviation_multiplier: 1.0,
      });
      setNewProfileName("");
      await fetchProfiles();
      // navigate to new profile
      nav(`/tv/${p.slug}`);
      setShowProfiles(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Falha ao criar perfil");
    } finally {
      setProfileBusy(false);
    }
  };

  const handleRenameProfile = async (slug) => {
    const name = renameDraft.trim();
    if (!name) return;
    setProfileBusy(true);
    try {
      const current = profiles.find((p) => p.slug === slug);
      if (!current) return;
      await updateTvProfile(slug, {
        name,
        slug: current.slug,
        target_intra_pct_of_cutting: current.target_intra_pct_of_cutting,
        target_between_per_cut_min: current.target_between_per_cut_min,
        intra_deviation_multiplier: current.intra_deviation_multiplier,
        between_deviation_multiplier: current.between_deviation_multiplier,
      });
      setRenamingSlug(null);
      setRenameDraft("");
      await Promise.all([fetchProfiles(), fetchLive()]);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Falha ao renomear");
    } finally {
      setProfileBusy(false);
    }
  };

  const handleDeleteProfile = async (slug) => {
    if (slug === "default") return;
    if (!window.confirm(`Apagar perfil "${slug}"? Esta acção não pode ser revertida.`)) return;
    setProfileBusy(true);
    try {
      await deleteTvProfile(slug);
      await fetchProfiles();
      if (activeSlug === slug) nav("/tv");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Falha ao apagar");
    } finally {
      setProfileBusy(false);
    }
  };

  const handleSwitchProfile = (slug) => {
    if (slug === activeSlug) return;
    if (slug === "default") nav("/tv");
    else nav(`/tv/${slug}`);
  };

  // Fetch initial + schedule-aware polling. The TV does TWO things in parallel:
  //  1) A short heartbeat poll every 60s — keeps "actualizado há Xs" fresh and
  //     picks up any out-of-band changes to the backend doc.
  //  2) A precise fetch a few seconds AFTER each scheduled scrape run — so the
  //     new data shows up as soon as the scraper finishes.
  useEffect(() => {
    let scheduleTimeout = null;
    let heartbeatInterval = null;
    let cancelled = false;
    const HEARTBEAT_MS = 60_000;          // 60s baseline poll
    const POST_SCHEDULE_GRACE_MS = 30_000; // wait 30s after the scheduled run for the scraper to finish

    const scheduleNextScheduledFetch = () => {
      if (cancelled) return;
      if (scheduleTimeout) clearTimeout(scheduleTimeout);
      const nextIso = liveRef.current?.next_scheduled_run;
      if (!nextIso) return; // no schedule, rely on heartbeat
      const ms = new Date(nextIso).getTime() - Date.now() + POST_SCHEDULE_GRACE_MS;
      if (ms <= 0 || ms > 6 * 60 * 60 * 1000) return; // skip if past or absurdly far
      scheduleTimeout = setTimeout(async () => {
        await fetchLive();
        scheduleNextScheduledFetch();
      }, ms);
    };

    // Initial fetch then start both pollers
    fetchLive().then(() => {
      fetchProfiles();
      fetchAvailableDays();
      scheduleNextScheduledFetch();
      heartbeatInterval = setInterval(async () => {
        if (cancelled) return;
        await fetchLive();
        // Re-align the schedule timer in case next_scheduled_run changed
        scheduleNextScheduledFetch();
      }, HEARTBEAT_MS);
    });

    return () => {
      cancelled = true;
      if (scheduleTimeout) clearTimeout(scheduleTimeout);
      if (heartbeatInterval) clearInterval(heartbeatInterval);
    };
  }, [fetchLive, fetchProfiles, fetchAvailableDays]);

  // wall clock (updates every second)
  useEffect(() => {
    clockRef.current = setInterval(() => setNow(new Date()), 1000);
    return () => { if (clockRef.current) clearInterval(clockRef.current); };
  }, []);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  const hasData = !!live?.has_data;
  // Profile colors + thresholds (from backend) with safe fallbacks
  const colors = live?.profile?.colors || {};
  const thresholds = live?.profile?.thresholds || {};
  const colorIntraGood = colors.intra_good || colors.intra || "#047857";
  const colorIntraWarn = colors.intra_warn || "#d97706";
  const colorIntraBad = colors.intra_bad || "#b91c1c";
  const colorBetweenMarkers = colors.between_markers || "#0369a1";
  const colorBetweenGood = colors.between_good || colors.between_markers || "#047857";
  const colorBetweenWarn = colors.between_warn || "#d97706";
  const colorBetweenBad = colors.between_bad || "#b91c1c";
  const colorFooterCutting = colors.footer_cutting || "#10b981";
  const colorFooterStopped = colors.footer_stopped || "#f59e0b";
  const colorFooterBetween = colors.footer_between || "#fb7185";
  // Legacy score-based intra thresholds (kept available; not used by the 3-tier UI)
  const thresholdIntraGood = Number(thresholds.intra_good_pct ?? 100);
  const thresholdIntraWarn = Number(thresholds.intra_warn_pct ?? 80);
  // 3-tier absolute thresholds (the real source of truth for the KPI colours)
  const tierIntraGoodPct = Number(thresholds.tier_intra_good_pct ?? 50);
  const tierIntraWarnPct = Number(thresholds.tier_intra_warn_pct ?? 75);
  const tierBetweenGoodMin = Number(thresholds.tier_between_good_min ?? 2.0);
  const tierBetweenWarnMin = Number(thresholds.tier_between_warn_min ?? 3.0);
  const cuts = live?.total_cuts ?? 0;
  const avgSpeed = live?.avg_cutting_speed ?? 0;
  const avgCycleSpeed = live?.avg_cycle_speed ?? 0;
  const avgBetween = live?.avg_between_markers ?? 0;
  const stoppedPct = live?.stopped_vs_cutting_pct ?? 0;
  const betweenPct = live?.between_vs_cutting_pct ?? 0;
  const pctCutting = live?.pct_cutting ?? 0;
  const pctStopped = live?.pct_stopped ?? 0;
  const pctBetween = live?.pct_between ?? 0;
  const targetEff = live?.target_efficiency_pct ?? 0;
  const scoreIntra = live?.score_intra_pct ?? 0;     // só depende da relação paragem/corte
  const scoreEntre = live?.score_entre_pct ?? 0;     // só depende do tempo entre marcadas
  const targetIntraPct = live?.target_intra_pct_of_cutting ?? 50;
  const targetBetweenMin = live?.target_between_per_cut_min ?? 2.0;
  const intraDevPct = live?.intra_deviation_pct ?? 0;          // pontos percentuais acima da meta
  const betweenDevSec = live?.between_deviation_sec ?? 0;      // segundos de desvio (positivos = mau)
  const betweenDevPct = live?.between_deviation_pct ?? 0;      // % de desvio relativo

  // Formatadores para os desvios
  const fmtDevSeconds = (s) => {
    const sign = s > 0 ? "+" : s < 0 ? "−" : "±";
    const abs = Math.abs(s);
    if (abs < 60) return `${sign}${abs}s`;
    const m = Math.floor(abs / 60);
    const r = abs % 60;
    return `${sign}${m}min${r > 0 ? String(r).padStart(2, "0") + "s" : ""}`;
  };
  const fmtDevPct = (p) => {
    const sign = p > 0 ? "+" : p < 0 ? "−" : "±";
    return `${sign}${Math.abs(p).toFixed(1)}%`;
  };
  // Decimal minutes → "M:SS" using 60-second minutes.
  const fmtMinToMSS = (m) => {
    if (m === null || m === undefined || isNaN(m)) return "—";
    const totalSec = Math.max(0, Math.round(Number(m) * 60));
    const mm = Math.floor(totalSec / 60);
    const ss = totalSec % 60;
    return `${mm}:${String(ss).padStart(2, "0")}`;
  };
  const devEntreColor = betweenDevSec <= 0 ? "text-emerald-600" : betweenDevPct <= 10 ? "text-amber-600" : "text-red-600";

  // Thresholds for the efficiency card (higher is better) — usa o score INTRA (independente do entre marcadas)
  const effColor = scoreIntra >= 80 ? "text-emerald-700"
                : scoreIntra >= 50 ? "text-amber-600"
                : "text-red-700";
  const effBar = scoreIntra >= 80 ? "bg-emerald-500"
              : scoreIntra >= 50 ? "bg-amber-500"
              : "bg-red-500";
  const effIconColor = scoreIntra >= 80 ? "text-emerald-600"
                    : scoreIntra >= 50 ? "text-amber-600"
                    : "text-red-600";

  const inProgress = !!live?.in_progress;
  const source = live?.source;

  // Hourly chart data — fill missing hours in 7..18 range with 0 cuts
  const cphMap = new Map((live?.cuts_per_hour || []).map((x) => [x.hour, x.cuts]));
  const hourRange = Array.from({ length: 12 }, (_, i) => i + 7); // 07..18
  const maxCph = Math.max(1, ...hourRange.map((h) => cphMap.get(h) || 0));
  const currentHour = now.getHours();

  return (
    <div className="h-screen w-full bg-stone-50 text-slate-900 flex flex-col overflow-hidden" data-testid="tv-page">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 lg:px-10 py-3 lg:py-4 border-b border-slate-200 bg-white shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-shrink">
          <img
            src={`${process.env.PUBLIC_URL || ""}/samidel-logo-dark.png`}
            alt="Samidel"
            data-testid="tv-brand-logo"
            className="h-7 lg:h-9 w-auto shrink-0 mr-2 select-none"
            draggable={false}
          />
          <div className="min-w-0">
            <div className="text-xs sm:text-sm lg:text-base font-bold tracking-tight text-slate-900 truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Máquina de Corte · {selectedDate ? fmtPrettyDate(selectedDate) : "Hoje"}
            </div>
            <div className="text-[9px] lg:text-[10px] uppercase tracking-[0.18em] text-slate-500 truncate">
              {selectedDate ? fmtDateFromIso(selectedDate) : fmtDate(now)}
            </div>
          </div>
          {/* Date picker — defaults to TODAY, can browse the days with data */}
          <div className="relative ml-2">
            <button
              onClick={() => setShowDatePicker((v) => !v)}
              data-testid="tv-date-picker-button"
              className={`inline-flex items-center gap-1.5 text-xs lg:text-sm px-3 py-1.5 rounded-md border font-medium transition-colors ${
                selectedDate
                  ? 'bg-sky-50 border-sky-300 text-sky-800 hover:bg-sky-100'
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'
              }`}
              title="Seleccionar dia para ver os dados (calendário com dias disponíveis)"
            >
              <CalendarIcon className="h-4 w-4" />
              {selectedDate ? selectedDate : "Hoje"}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {showDatePicker && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowDatePicker(false)} />
                <div className="absolute left-0 top-full mt-1.5 z-50 w-72 max-h-80 overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-200 py-1" data-testid="tv-date-picker-list">
                  <button
                    onClick={() => { setSelectedDate(null); setShowDatePicker(false); }}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-emerald-50 ${!selectedDate ? 'bg-emerald-50 text-emerald-800 font-semibold' : 'text-slate-700'}`}
                    data-testid="tv-date-picker-today"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Radio className={`h-3.5 w-3.5 ${!selectedDate ? 'text-emerald-600' : 'text-slate-400'}`} />
                      Hoje (em directo)
                    </span>
                    {!selectedDate && <Check className="h-4 w-4 text-emerald-600" />}
                  </button>
                  {availableDays.length > 0 && (
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold border-t border-slate-100 mt-1">
                      Dias com dados ({availableDays.length})
                    </div>
                  )}
                  {availableDays.map((d) => {
                    const isSelected = selectedDate === d.date;
                    const cuts = d?.metrics?.total_cuts || 0;
                    return (
                      <button
                        key={d.date}
                        onClick={() => { setSelectedDate(d.date); setShowDatePicker(false); }}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-sky-50 ${isSelected ? 'bg-sky-50 text-sky-800 font-semibold' : 'text-slate-700'}`}
                        data-testid={`tv-date-picker-day-${d.date}`}
                      >
                        <span className="inline-flex items-center gap-2 min-w-0">
                          <CalendarIcon className={`h-3.5 w-3.5 shrink-0 ${isSelected ? 'text-sky-600' : 'text-slate-400'}`} />
                          <span className="font-mono tabular-nums">{d.date}</span>
                          <span className="text-[11px] text-slate-500 truncate">· {cuts} cortes</span>
                        </span>
                        {isSelected && <Check className="h-4 w-4 text-sky-600 shrink-0" />}
                      </button>
                    );
                  })}
                  {availableDays.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500 italic">Sem dias com dados.</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:gap-5 xl:gap-7 justify-end">
          {/* Último corte realizado pela máquina — grande e destacado */}
          {hasData && live?.last_cut_end && (
            <div
              className="flex items-center gap-3 px-4 lg:px-5 py-2.5 lg:py-3 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/60 border-2 border-emerald-300 shadow-[0_4px_16px_-6px_rgba(16,185,129,0.35)]"
              data-testid="tv-last-task"
              title="Hora do último corte concluído pela máquina"
            >
              <div className="h-10 w-10 lg:h-12 lg:w-12 rounded-xl bg-emerald-500/15 grid place-items-center shrink-0">
                <Scissors className="h-6 w-6 lg:h-7 lg:w-7 text-emerald-700" />
              </div>
              <div className="flex flex-col leading-none">
                <div className="text-[10px] lg:text-xs uppercase tracking-[0.18em] text-emerald-700/80 font-bold mb-1">
                  Último corte
                </div>
                <div className="flex items-baseline gap-2">
                  <span
                    className="font-extrabold tabular-nums text-emerald-800 leading-none"
                    style={{
                      fontSize: "clamp(2rem, 3.6vw, 3.8rem)",
                      fontFamily: "'JetBrains Mono', monospace",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {(live.last_cut_end || '').slice(0, 5)}
                  </span>
                  <span className="text-sm lg:text-base text-emerald-700/80 font-semibold" data-testid="tv-last-task-ago">
                    {fmtAgoFromHHMMSS(live.last_cut_end, now)}
                  </span>
                </div>
              </div>
            </div>
          )}
          {/* Relógio + Próxima leitura empilhados verticalmente */}
          <div
            className="flex flex-col items-end gap-0.5 px-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200"
            data-testid="tv-clock-combined"
            title="Hora actual / Próxima leitura agendada"
          >
            <div className="flex items-center gap-2">
              <div className="font-mono text-base lg:text-lg tracking-tight tabular-nums text-slate-900 font-bold leading-none" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {fmtClock(now)}
              </div>
              <div className="text-[10px] text-slate-500 flex items-center gap-1">
                <RefreshCw className={`h-2.5 w-2.5 ${refreshing ? "animate-spin text-emerald-600" : ""}`} />
                <span className="truncate">act. {fmtAgo(live?.uploaded_at)}</span>
              </div>
            </div>
            <div className="w-full h-px bg-slate-300" />
            <div className="flex items-center gap-1.5 self-end" data-testid="tv-countdown">
              <Hourglass className={`h-3 w-3 text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
              <span className="text-[9px] uppercase tracking-wider text-slate-500">Próxima leitura</span>
              <span
                className="font-mono text-sm lg:text-base tabular-nums text-slate-800 font-bold leading-none"
                data-testid="tv-next-run-clock"
              >
                {refreshing ? "…" : (live?.next_scheduled_run ? fmtClockHM(live.next_scheduled_run) : "—")}
              </span>
              {live?.next_scheduled_run && (
                <span className="text-[10px] text-slate-500" data-testid="tv-next-run-relative">
                  {fmtInFuture(live.next_scheduled_run, now)}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => setAdjusted((v) => !v)}
            data-testid="tv-adjusted-toggle"
            className={`relative p-2 rounded-md border font-medium inline-flex items-center justify-center transition-colors ${
              adjusted
                ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'
            }`}
            title={adjusted
              ? 'A mostrar dados AJUSTADOS (excluindo períodos de anomalia reportada). Clica para ver Reais.'
              : 'A mostrar dados REAIS. Clica para ver ajustados (sem anomalias reportadas).'}
            aria-label={adjusted ? 'Ajustado' : 'Real'}
          >
            <AlertTriangle className="h-4 w-4" />
            {adjusted && (live?.anomaly_windows_count > 0 || live?.justifications_applied > 0) && (
              <span
                className="absolute -top-1.5 -right-1.5 px-1 py-0 rounded-full text-[9px] leading-tight bg-amber-500 text-white tabular-nums font-bold border border-white"
                title={`${live?.anomaly_windows_count || 0} anomalias · ${live?.justifications_applied || 0} justificações`}
              >
                {(live?.anomaly_windows_count || 0) + (live?.justifications_applied || 0)}
              </span>
            )}
          </button>
          <button
            onClick={openSettings}
            data-testid="tv-settings-button"
            className="p-2 rounded-md border border-slate-300 hover:bg-slate-100 text-slate-700 inline-flex items-center justify-center"
            title="Configurar metas de eficiência deste perfil"
            aria-label="Metas"
          >
            <Settings className="h-4 w-4" />
          </button>
          <button
            onClick={goFullscreen}
            data-testid="tv-fullscreen-button"
            className="p-2 rounded-md border border-slate-300 hover:bg-slate-100 text-slate-700 inline-flex items-center justify-center"
            title="Ecrã inteiro"
            aria-label="Ecrã inteiro"
          >
            <Tv className="h-4 w-4" />
          </button>
          <button
            onClick={() => nav('/cutting-simulator')}
            data-testid="tv-close-button"
            className="p-2 rounded-md border border-slate-300 hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-slate-700 inline-flex items-center justify-center"
            title="Sair do modo TV"
            aria-label="Sair"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Body — 2×2 grid of giant KPIs */}
      <main className="flex-1 min-h-0 p-3 lg:p-4 grid grid-cols-1 md:grid-cols-2 md:grid-rows-2 gap-3 lg:gap-4 overflow-hidden">
        <DualKPI
          icon={Gauge}
          label="Velocidade de Corte"
          leftLabel="Média de Corte"
          leftValue={hasData ? (avgCycleSpeed > 0 ? avgCycleSpeed.toFixed(1) : "—") : "—"}
          leftUnit="m/min"
          leftColor="text-amber-600"
          leftAccent="bg-amber-500"
          rightLabel="Real de Corte"
          rightValue={hasData ? avgSpeed.toFixed(1) : "—"}
          rightUnit="m/min"
          rightColor="text-emerald-700"
          rightAccent="bg-emerald-500"
          leftFontVw={10}
          rightFontVw={7}
          testid="tv-avg-cutting-speed"
        />
        <BigKPI
          icon={Timer}
          label="T. M. Entre Marcadas"
          value={hasData ? fmtMinToMSS(avgBetween) : "—"}
          unit="min:s/corte"
          metaTag={hasData ? `Meta ≤${fmtMinToMSS(tierBetweenGoodMin)} min:s` : null}
          subText={null}
          customColor={
            avgBetween <= tierBetweenGoodMin ? colorBetweenGood
            : avgBetween <= tierBetweenWarnMin ? colorBetweenWarn
            : colorBetweenBad
          }
          valueFontVw={11}
          testid="tv-avg-between-markers"
        />
        <BigKPI
          icon={Scissors}
          label="A Cortar · Acumulado Dia"
          value={hasData ? fmtMinutes(live?.total_cutting_time || 0) : "—"}
          unit={hasData ? `${pctCutting.toFixed(1)}%` : ""}
          subText={null}
          customColor="#0f172a"
          valueFontVw={11}
          unitFontVw={7}
          testid="tv-cuts-today"
        />
        <BigKPI
          icon={Target}
          label="Intra Marcadas"
          value={hasData ? `${scoreIntra.toFixed(1)}` : "—"}
          unit={hasData ? (
            <span className="inline-flex items-baseline gap-1.5">
              <span style={{ fontSize: "0.55em" }}>%</span>
              <span className="uppercase tracking-[0.12em] font-bold text-slate-500" style={{ fontSize: "0.28em" }}>Eficiência</span>
            </span>
          ) : "%"}
          metaTag={hasData ? `Meta ≤${targetIntraPct.toFixed(0)}% paragem/corte` : null}
          subText={hasData ? (
            <span>
              <span className="text-slate-500">Paragem actual:</span>{" "}
              <span className={`font-bold tabular-nums ${stoppedPct <= targetIntraPct ? 'text-emerald-700' : 'text-red-700'}`} style={{ fontSize: "1.15em" }}>
                {stoppedPct.toFixed(1)}%
              </span>
              <span className="text-slate-400 text-[0.85em]"> da máquina a cortar</span>
            </span>
          ) : null}
          valueColor={effColor}
          iconColor={effIconColor}
          accentBar={effBar}
          customColor={
            scoreIntra >= thresholdIntraGood ? colorIntraGood
            : scoreIntra >= thresholdIntraWarn ? colorIntraWarn
            : colorIntraBad
          }
          valueFontVw={11}
          unitFontVw={4}
          testid="tv-target-efficiency"
        />
      </main>

      {/* Footer: time-distribution percentages — grandes, visuais, com pílulas coloridas */}
      <footer className="border-t border-slate-200 bg-white shrink-0" data-testid="tv-footer">
        {/* Segmented bar (mais espessa, com cores do perfil) */}
        {hasData && (
          <div className="h-5 lg:h-6 w-full flex">
            <div style={{ width: `${pctCutting}%`, background: colorFooterCutting }} title={`Corte: ${pctCutting}%`} />
            <div style={{ width: `${pctStopped}%`, background: colorFooterStopped }} title={`Parada: ${pctStopped}%`} />
            <div style={{ width: `${pctBetween}%`, background: colorFooterBetween }} title={`Entre marcadas: ${pctBetween}%`} />
          </div>
        )}
        <div className="px-6 lg:px-10 py-2 lg:py-3">
          {hasData ? (
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-3 lg:gap-4">
              {/* CORTES — pílula em destaque (preto) + Perímetro à direita */}
              <div
                className="relative rounded-2xl px-3 lg:px-4 py-2 lg:py-3 flex items-center gap-3 lg:gap-4 overflow-hidden border-2 border-slate-800 bg-slate-900"
                data-testid="tv-pct-cuts"
              >
                <span className="absolute left-0 top-0 bottom-0 w-2 bg-slate-100" />
                <div className="h-9 w-9 lg:h-10 lg:w-10 rounded-lg grid place-items-center shrink-0 bg-slate-700">
                  <Scissors className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="uppercase tracking-[0.18em] font-bold text-slate-300" style={{ fontSize: "clamp(0.6rem, 0.9vmin, 0.85rem)" }}>
                    Cortes Hoje
                  </div>
                  <div className="flex items-baseline gap-1.5 leading-none mt-0.5">
                    <span
                      className="font-extrabold tabular-nums leading-none text-white"
                      style={{
                        fontSize: "clamp(1.5rem, 3.5vmin, 2.6rem)",
                        fontFamily: "'Space Grotesk', sans-serif",
                        letterSpacing: "-0.03em",
                      }}
                    >
                      {cuts.toLocaleString("pt-PT")}
                    </span>
                    <span className="font-semibold text-slate-400" style={{ fontSize: "clamp(0.7rem, 1.1vmin, 0.95rem)" }}>
                      {cuts === 1 ? 'corte' : 'cortes'}
                    </span>
                  </div>
                </div>
                {/* Perímetro à DIREITA dos cortes, em METROS */}
                <div
                  className="flex flex-col items-end justify-center pl-3 lg:pl-4 border-l border-slate-700 shrink-0"
                  data-testid="tv-perimeter"
                >
                  <div className="uppercase tracking-[0.18em] font-bold text-slate-400" style={{ fontSize: "clamp(0.55rem, 0.85vmin, 0.8rem)" }}>
                    Perímetro
                  </div>
                  <div className="flex items-baseline gap-1 leading-none mt-0.5">
                    <span
                      className="font-extrabold tabular-nums leading-none text-white"
                      style={{
                        fontSize: "clamp(1.2rem, 2.8vmin, 2rem)",
                        fontFamily: "'Space Grotesk', sans-serif",
                        letterSpacing: "-0.03em",
                      }}
                    >
                      {Math.round(live?.total_perimeter || 0).toLocaleString('pt-PT')}
                    </span>
                    <span className="font-semibold text-slate-400" style={{ fontSize: "clamp(0.65rem, 1vmin, 0.9rem)" }}>m</span>
                  </div>
                </div>
              </div>

              {/* INTRA MARCADAS */}
              <div
                className="relative rounded-2xl px-3 lg:px-4 py-2 lg:py-3 flex items-center gap-3 lg:gap-4 overflow-hidden border-2"
                style={{
                  background: `linear-gradient(135deg, ${hexA(colorFooterStopped, 0.10)}, ${hexA(colorFooterStopped, 0.04)})`,
                  borderColor: hexA(colorFooterStopped, 0.35),
                }}
                data-testid="tv-pct-stopped"
              >
                <span className="absolute left-0 top-0 bottom-0 w-2" style={{ background: colorFooterStopped }} />
                <div className="h-9 w-9 lg:h-10 lg:w-10 rounded-lg grid place-items-center shrink-0" style={{ background: hexA(colorFooterStopped, 0.18) }}>
                  <span className="h-4 w-4 lg:h-5 lg:w-5 rounded-md" style={{ background: colorFooterStopped }} />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="uppercase tracking-[0.18em] font-bold" style={{ color: colorFooterStopped, fontSize: "clamp(0.6rem, 0.9vmin, 0.85rem)" }}>
                    Intra Marcadas
                  </div>
                  <div className="flex items-baseline gap-1.5 leading-none mt-0.5">
                    <span
                      className="font-extrabold tabular-nums leading-none"
                      style={{
                        fontSize: "clamp(1.4rem, 3vmin, 2.4rem)",
                        fontFamily: "'Space Grotesk', sans-serif",
                        letterSpacing: "-0.025em",
                        color: colorFooterStopped,
                      }}
                    >
                      {fmtMinutes(live?.total_machine_stopped || 0)}
                    </span>
                    <span className="font-semibold tabular-nums" style={{ color: hexA(colorFooterStopped, 0.75), fontSize: "clamp(0.7rem, 1.1vmin, 0.95rem)" }}>
                      · {pctStopped.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* ENTRE MARCADAS */}
              <div
                className="relative rounded-2xl px-3 lg:px-4 py-2 lg:py-3 flex items-center gap-3 lg:gap-4 overflow-hidden border-2"
                style={{
                  background: `linear-gradient(135deg, ${hexA(colorFooterBetween, 0.10)}, ${hexA(colorFooterBetween, 0.04)})`,
                  borderColor: hexA(colorFooterBetween, 0.35),
                }}
                data-testid="tv-pct-between"
              >
                <span className="absolute left-0 top-0 bottom-0 w-2" style={{ background: colorFooterBetween }} />
                <div className="h-9 w-9 lg:h-10 lg:w-10 rounded-lg grid place-items-center shrink-0" style={{ background: hexA(colorFooterBetween, 0.18) }}>
                  <span className="h-4 w-4 lg:h-5 lg:w-5 rounded-md" style={{ background: colorFooterBetween }} />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="uppercase tracking-[0.18em] font-bold" style={{ color: colorFooterBetween, fontSize: "clamp(0.6rem, 0.9vmin, 0.85rem)" }}>
                    Entre Marcadas
                  </div>
                  <div className="flex items-baseline gap-1.5 leading-none mt-0.5">
                    <span
                      className="font-extrabold tabular-nums leading-none"
                      style={{
                        fontSize: "clamp(1.4rem, 3vmin, 2.4rem)",
                        fontFamily: "'Space Grotesk', sans-serif",
                        letterSpacing: "-0.025em",
                        color: colorFooterBetween,
                      }}
                    >
                      {fmtMinutes(live?.total_between_markers || 0)}
                    </span>
                    <span className="font-semibold tabular-nums" style={{ color: hexA(colorFooterBetween, 0.75), fontSize: "clamp(0.7rem, 1.1vmin, 0.95rem)" }}>
                      · {pctBetween.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-slate-500 text-center py-2">Sem dados de hoje. A aguardar primeiro scraping…</div>
          )}
          <div className="text-slate-400 text-[10px] lg:text-xs text-center mt-1.5">
            {error
              ? <span className="text-red-600 font-medium">Erro: {error}</span>
              : (live?.next_scheduled_run
                  ? <>Próxima actualização às <span className="font-semibold text-slate-600 tabular-nums">{fmtClockHM(live.next_scheduled_run)}</span> ({fmtInFuture(live.next_scheduled_run, now)})</>
                  : <>Sem rotina agendada a alimentar a TV — atualização manual</>)
            }
          </div>
        </div>
      </footer>

      {/* Profiles management modal */}
      {showProfiles && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="tv-profiles-modal">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <Tv className="h-5 w-5 text-slate-600" />
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Perfis de TV
                </h3>
              </div>
              <button
                onClick={() => { setShowProfiles(false); setRenamingSlug(null); setRenameDraft(""); }}
                className="p-1 rounded hover:bg-slate-200 text-slate-600"
                data-testid="tv-profiles-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-xs text-slate-500 leading-relaxed">
                Cada perfil é uma <strong>TV independente</strong> com as suas próprias metas. Usa-o quando tens várias máquinas/locais com objectivos diferentes.
                Cada perfil tem um URL próprio: <code className="font-mono text-[11px] bg-slate-100 px-1.5 py-0.5 rounded">/tv/&lt;slug&gt;</code> — abre esse URL na TV do chão de fábrica.
              </p>

              {/* List */}
              <div className="space-y-2">
                {profiles.map((p) => {
                  const isActive = p.slug === activeSlug;
                  const isDefault = p.slug === "default";
                  const isRenaming = renamingSlug === p.slug;
                  return (
                    <div
                      key={p.slug}
                      className={`rounded-xl border-2 p-3 transition-colors ${isActive ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
                      data-testid={`tv-profile-row-${p.slug}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${isActive ? "bg-emerald-500/15 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {isActive ? <Check className="h-4 w-4" /> : <Tv className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          {isRenaming ? (
                            <input
                              type="text"
                              value={renameDraft}
                              onChange={(e) => setRenameDraft(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") handleRenameProfile(p.slug); if (e.key === "Escape") { setRenamingSlug(null); setRenameDraft(""); } }}
                              autoFocus
                              className="w-full px-2 py-1 rounded-md border border-emerald-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                              data-testid={`tv-profile-rename-input-${p.slug}`}
                            />
                          ) : (
                            <>
                              <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                                {p.name}
                                {isDefault && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-bold">predef.</span>}
                                {isActive && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-700 font-bold">activo</span>}
                              </div>
                              <div className="text-[11px] text-slate-500 font-mono truncate">
                                /tv{p.slug === "default" ? "" : "/" + p.slug} · intra ≤{p.target_intra_pct_of_cutting}% · entre {p.target_between_per_cut_min}min
                              </div>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {isRenaming ? (
                            <>
                              <button
                                onClick={() => handleRenameProfile(p.slug)}
                                disabled={profileBusy}
                                className="p-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                                data-testid={`tv-profile-rename-save-${p.slug}`}
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => { setRenamingSlug(null); setRenameDraft(""); }}
                                className="p-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                              {!isActive && (
                                <button
                                  onClick={() => { handleSwitchProfile(p.slug); setShowProfiles(false); }}
                                  className="text-[11px] px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 font-medium"
                                  data-testid={`tv-profile-activate-${p.slug}`}
                                >
                                  Activar
                                </button>
                              )}
                              <button
                                onClick={() => { setRenamingSlug(p.slug); setRenameDraft(p.name); }}
                                className="p-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100"
                                title="Renomear"
                                data-testid={`tv-profile-rename-${p.slug}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteProfile(p.slug)}
                                disabled={isDefault || profileBusy}
                                className="p-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-red-50 hover:border-red-200 hover:text-red-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-600 disabled:cursor-not-allowed"
                                title={isDefault ? "O perfil predefinido não pode ser apagado" : "Apagar perfil"}
                                data-testid={`tv-profile-delete-${p.slug}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Create new */}
              <div className="rounded-xl border-2 border-dashed border-slate-300 p-3 bg-slate-50/40">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block mb-2">Criar novo perfil</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleCreateProfile(); }}
                    placeholder="Ex.: Rex 50ES Linha 2"
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm font-medium focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 outline-none"
                    data-testid="tv-profile-new-input"
                  />
                  <button
                    onClick={handleCreateProfile}
                    disabled={profileBusy || !newProfileName.trim()}
                    className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                    data-testid="tv-profile-new-create"
                  >
                    <Plus className="h-4 w-4" /> Criar
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  Após criar, ajusta as metas no botão <strong>Metas</strong> e abre <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">/tv/&lt;slug&gt;</code> na TV correspondente.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end px-5 py-3 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => { setShowProfiles(false); setRenamingSlug(null); setRenameDraft(""); }}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 font-medium"
                data-testid="tv-profiles-done"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="tv-settings-modal">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2 min-w-0">
                <Settings className="h-5 w-5 text-slate-600 shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900 truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Metas · <span className="text-emerald-700">{live?.profile?.name || "Predefinido"}</span>
                  </h3>
                  <div className="text-[11px] text-slate-500 tabular-nums">slug: <span className="font-mono">{activeSlug}</span></div>
                </div>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1 rounded hover:bg-slate-200 text-slate-600 shrink-0"
                data-testid="tv-settings-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
              <p className="text-xs text-slate-500 leading-relaxed">
                A cor de cada KPI é decidida pelos <strong>3 patamares</strong> em baixo (Bom / Aviso / Mau).
                O patamar <strong>Bom</strong> é a meta — quando o valor actual passa do limite Aviso, o KPI fica vermelho.
              </p>

              {/* Color presets — collapsible (hidden by default to keep the modal short) */}
              <div className="rounded-xl border border-slate-300 bg-white" data-testid="tv-settings-presets">
                <button
                  type="button"
                  onClick={() => setShowColorPresets((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-xl"
                  data-testid="tv-settings-presets-toggle"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Presets de cor</span>
                    <span className="text-[10px] text-slate-400 truncate">({COLOR_PRESETS.length} paletas)</span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${showColorPresets ? 'rotate-180' : ''}`} />
                </button>
                {showColorPresets && (
                  <div className="px-3 pb-3 space-y-2 border-t border-slate-200 pt-3">
                    <p className="text-[11px] text-slate-500">Aplica uma paleta completa com um clique. As cores ficam no rascunho — confirma com <strong>Guardar</strong> em baixo.</p>
                    <div className="grid grid-cols-1 gap-1.5">
                      {COLOR_PRESETS.map((preset) => {
                        const isActive = preset.id === "verde-classico"
                          ? settingsDraft.color_intra_good === preset.colors.color_intra_good && settingsDraft.color_between_markers === preset.colors.color_between_markers
                          : Object.entries(preset.colors).every(([k, v]) => (settingsDraft[k] || "").toLowerCase() === v.toLowerCase());
                        return (
                          <button
                            key={preset.id}
                            type="button"
                            onClick={() => setSettingsDraft((s) => ({ ...s, ...preset.colors }))}
                            className={`group flex items-center gap-3 px-2.5 py-2 rounded-lg border text-left transition-colors ${isActive ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}
                            data-testid={`tv-settings-preset-${preset.id}`}
                          >
                            <div className="flex gap-0.5 shrink-0">
                              {preset.swatches.map((c, i) => (
                                <span key={i} className="h-6 w-3 rounded-sm border border-white shadow-sm" style={{ background: c }} />
                              ))}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                                {preset.label}
                                {isActive && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-700 font-bold">activo</span>}
                              </div>
                              <div className="text-[10px] text-slate-500 truncate">{preset.hint}</div>
                            </div>
                            {!isActive && (
                              <span className="text-[10px] text-slate-400 group-hover:text-slate-600 font-medium uppercase tracking-wider">aplicar</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Intra Marcadas — Meta de eficiência + 3 patamares de score */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
                <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Intra Marcadas · Meta de eficiência</div>
                <p className="text-[11px] text-slate-500 -mt-1">
                  O KPI mostra a <strong>eficiência</strong> (0–100%+). Defines a meta de paragem aceitável e o multiplicador da penalização; depois escolhes os 3 patamares de cor sobre o score resultante.
                </p>
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div>
                    <label className="text-[11px] text-slate-600 font-medium">Meta paragem ≤ (%)</label>
                    <input
                      type="number" min="1" max="500" step="1"
                      value={settingsDraft.target_intra_pct_of_cutting}
                      onChange={(e) => setSettingsDraft((s) => ({ ...s, target_intra_pct_of_cutting: e.target.value }))}
                      className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                      data-testid="tv-settings-target-intra"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-600 font-medium">Multiplicador (×)</label>
                    <input
                      type="number" min="0" max="20" step="0.1"
                      value={settingsDraft.intra_deviation_multiplier}
                      onChange={(e) => setSettingsDraft((s) => ({ ...s, intra_deviation_multiplier: e.target.value }))}
                      className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                      data-testid="tv-settings-intra-multiplier"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 -mt-1">
                  Cada <strong>1 pp</strong> acima da meta penaliza <strong>{Number(settingsDraft.intra_deviation_multiplier) || 0}%</strong>. Quando o actual é melhor que a meta, o score sobe acima de 100%.
                </p>
                <div className="border-t border-slate-200 pt-2 space-y-2">
                  <div className="text-[11px] text-slate-600 font-semibold">Patamares de cor (sobre o score)</div>
                  <div className="grid grid-cols-2 gap-2 items-end">
                    <div>
                      <label className="text-[11px] text-slate-600 font-medium">Bom: score ≥ (%)</label>
                      <input
                        type="number" min="0" step="1"
                        value={settingsDraft.threshold_intra_good_pct}
                        onChange={(e) => setSettingsDraft((s) => ({ ...s, threshold_intra_good_pct: e.target.value }))}
                        className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                        data-testid="tv-settings-threshold-intra-good"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-600 font-medium">Aviso: score ≥ (%)</label>
                      <input
                        type="number" min="0" step="1"
                        value={settingsDraft.threshold_intra_warn_pct}
                        onChange={(e) => setSettingsDraft((s) => ({ ...s, threshold_intra_warn_pct: e.target.value }))}
                        className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                        data-testid="tv-settings-threshold-intra-warn"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[
                      { key: "color_intra_good", label: `Cor BOM (score ≥ ${settingsDraft.threshold_intra_good_pct}%)` },
                      { key: "color_intra_warn", label: `Cor AVISO (score ≥ ${settingsDraft.threshold_intra_warn_pct}%)` },
                      { key: "color_intra_bad",  label: `Cor MAU (score < ${settingsDraft.threshold_intra_warn_pct}%)` },
                    ].map((row) => (
                      <div key={row.key} className="flex items-center gap-3" data-testid={`tv-settings-${row.key.replace(/_/g, '-')}`}>
                        <input
                          type="color"
                          value={settingsDraft[row.key] || "#000000"}
                          onChange={(e) => setSettingsDraft((s) => ({ ...s, [row.key]: e.target.value }))}
                          className="h-9 w-11 rounded-md border border-slate-300 cursor-pointer p-0.5 bg-white"
                        />
                        <div className="flex-1 min-w-0 text-xs font-medium text-slate-800">{row.label}</div>
                        <input
                          type="text"
                          value={settingsDraft[row.key] || ""}
                          onChange={(e) => setSettingsDraft((s) => ({ ...s, [row.key]: e.target.value }))}
                          placeholder="#10b981"
                          className="w-24 px-2 py-1 rounded-md border border-slate-300 text-xs font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Patamares de cor 3 tiers — Entre Marcadas */}
              <div className="rounded-xl border border-slate-300 bg-slate-50/60 p-3 space-y-3">
                <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Patamares de cor · Entre Marcadas</div>
                <p className="text-[11px] text-slate-500 -mt-1">
                  Cor do número grande conforme o <strong>tempo médio entre marcadas</strong> (min/corte). Verde se ≤ limite Bom, amarelo se ≤ limite Aviso, vermelho acima.
                </p>
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div>
                    <label className="text-[11px] text-slate-600 font-medium">Bom: ≤ (min)</label>
                    <input
                      type="number" min="0" step="0.1"
                      value={settingsDraft.tier_between_good_min}
                      onChange={(e) => setSettingsDraft((s) => ({ ...s, tier_between_good_min: e.target.value }))}
                      className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                      data-testid="tv-settings-tier-between-good"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-600 font-medium">Aviso: ≤ (min)</label>
                    <input
                      type="number" min="0" step="0.1"
                      value={settingsDraft.tier_between_warn_min}
                      onChange={(e) => setSettingsDraft((s) => ({ ...s, tier_between_warn_min: e.target.value }))}
                      className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                      data-testid="tv-settings-tier-between-warn"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    { key: "color_between_good", label: `Cor BOM (≤ ${settingsDraft.tier_between_good_min} min)` },
                    { key: "color_between_warn", label: `Cor AVISO (≤ ${settingsDraft.tier_between_warn_min} min)` },
                    { key: "color_between_bad",  label: `Cor MAU (> ${settingsDraft.tier_between_warn_min} min)` },
                  ].map((row) => (
                    <div key={row.key} className="flex items-center gap-3" data-testid={`tv-settings-${row.key.replace(/_/g, '-')}`}>
                      <input
                        type="color"
                        value={settingsDraft[row.key] || "#000000"}
                        onChange={(e) => setSettingsDraft((s) => ({ ...s, [row.key]: e.target.value }))}
                        className="h-9 w-11 rounded-md border border-slate-300 cursor-pointer p-0.5 bg-white"
                      />
                      <div className="flex-1 min-w-0 text-xs font-medium text-slate-800">{row.label}</div>
                      <input
                        type="text"
                        value={settingsDraft[row.key] || ""}
                        onChange={(e) => setSettingsDraft((s) => ({ ...s, [row.key]: e.target.value }))}
                        placeholder="#10b981"
                        className="w-24 px-2 py-1 rounded-md border border-slate-300 text-xs font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Outras cores */}
              <div className="rounded-xl border border-slate-300 bg-slate-50/60 p-3 space-y-3">
                <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Outras cores · Rodapé</div>

                {[
                  { key: "color_footer_cutting", label: "Rodapé · Corte", hint: "Cor do bloco de Corte" },
                  { key: "color_footer_stopped", label: "Rodapé · Parada", hint: "Cor do bloco de Parada" },
                  { key: "color_footer_between", label: "Rodapé · Entre Marcadas", hint: "Cor do bloco Entre Marcadas" },
                ].map((row) => (
                  <div key={row.key} className="flex items-center gap-3" data-testid={`tv-settings-${row.key.replace(/_/g, '-')}`}>
                    <input
                      type="color"
                      value={settingsDraft[row.key] || "#000000"}
                      onChange={(e) => setSettingsDraft((s) => ({ ...s, [row.key]: e.target.value }))}
                      className="h-10 w-12 rounded-md border border-slate-300 cursor-pointer p-0.5 bg-white"
                      data-testid={`tv-settings-${row.key.replace(/_/g, '-')}-picker`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-800">{row.label}</div>
                      <div className="text-[11px] text-slate-500">{row.hint}</div>
                    </div>
                    <input
                      type="text"
                      value={settingsDraft[row.key] || ""}
                      onChange={(e) => setSettingsDraft((s) => ({ ...s, [row.key]: e.target.value }))}
                      placeholder="#10b981"
                      className="w-24 px-2 py-1.5 rounded-md border border-slate-300 text-xs font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setShowSettings(false)}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 font-medium"
                data-testid="tv-settings-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="px-4 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 font-medium disabled:opacity-60"
                data-testid="tv-settings-save"
              >
                {savingSettings ? "A guardar…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
